import React, { useEffect, useState } from 'react';
import { Fuel, Menu, X } from 'lucide-react';
import Button from './ui/Button';

const LANDING_LINKS = [
  { href: '#funcionalidades', label: 'Funcionalidades' },
  { href: '#beneficios', label: 'Por que Gas2' },
  { href: '#screenshots', label: 'Screenshots' },
  { href: '#download', label: 'Download' },
  { href: '/blog', label: 'Blog' },
  { href: '/ferramentas', label: 'Ferramentas' },
  { href: '/quiz', label: 'Quiz' },
];

const BLOG_LINKS = [
  { href: '/', label: 'Início' },
  { href: '/blog', label: 'Blog' },
  { href: '/ferramentas', label: 'Ferramentas' },
  { href: '/quiz', label: 'Quiz' },
];

interface NavbarProps {
  /** Força o estilo sólido (fundo branco) independente do scroll — use em páginas com fundo escuro */
  solid?: boolean;
}

// Navbar fixa com fundo transparente que muda para branco ao rolar a página
const Navbar: React.FC<NavbarProps> = ({ solid = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isBlogRoute =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/blog');
  const isBlogPost =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/blog/');
  const isAppRoute =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/app/');
  const isToolsRoute =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/ferramentas');
  const isQuizRoute =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/quiz');
  const NAV_LINKS = isBlogRoute || isAppRoute || isToolsRoute || isQuizRoute ? BLOG_LINKS : LANDING_LINKS;

  // Destino do botão "Baixar App" conforme rota:
  //  - artigo        → rola até o CTA no final do post
  //  - listagem blog → landing page seção download
  //  - ferramentas   → landing page seção download
  //  - /app/*        → landing page seção download
  //  - landing       → scroll suave até #download
  const downloadHref = isBlogPost
    ? '#cta-download'
    : isBlogRoute || isAppRoute || isToolsRoute || isQuizRoute
    ? '/#download'
    : '#download';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || solid
          ? 'border-b border-gray-100 bg-white/95 shadow-sm backdrop-blur-md'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between lg:h-[68px]">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-400">
              <Fuel className="h-5 w-5 text-gray-900" strokeWidth={2} />
            </span>
            <span className="font-display">Gas2</span>
          </a>

          {/* Links desktop */}
          <nav className="hidden items-center gap-7 md:flex">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-gray-600 transition-colors duration-200 hover:text-amber-500"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA desktop */}
          <div className="hidden md:block">
            <Button href={downloadHref} variant="primary" size="sm">
              Baixar App
            </Button>
          </div>

          {/* Botão mobile */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="rounded-lg p-2 text-gray-700 transition hover:bg-gray-100 md:hidden"
            aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Menu mobile expandido */}
      {mobileOpen && (
        <div className="border-t border-gray-100 bg-white px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-amber-50 hover:text-amber-600"
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="mt-3 pt-3 border-t border-gray-100">
            <Button href={downloadHref} variant="primary" size="sm" className="w-full">
              Baixar App
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;

